// ─────────────────────────────────────────────────────────────────────────────
// useCalendar — GET /api/rexy/calendar + /api/rexy/calendar/day.
// Shared core (owner ruling 2026-07-31, "one core, every window").
//
// A window on what the harness already holds: click a date and that day's
// NOTES / REPORTS open beside its timestamped activity. Month navigation and
// day selection are GATES, so they live here — the desktop renders them through
// react-calendar and the phone through its own 7-column grid, but both ask the
// same questions of the same endpoints and get the same honest answers.
//
// GHOST HOLD item 1 — `monthStatus` (per-store source status) is part of the
// contract, not decoration: an unreadable store must never look like a quiet
// month. Every surface that renders the grid MUST also render these.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useState } from 'react';
import { authHeaders } from './authHeaders';
import { calendar, calendarDay } from './endpoints';

export type DayCounts = { events: number; reports: number; notes: number; snapshot: string };

export type CalendarTab = 'notes' | 'reports';

// Local calendar-cell date key (the clicked cell's label is authoritative; the
// backend interprets it in the USER-SET timezone — never toISOString/UTC).
export function dayKey(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export type CalendarFeed = {
  monthDays: Record<string, DayCounts>;
  /** Per-store read status. Anything not ok/fresh MUST surface as a visible warning. */
  monthStatus: Record<string, string>;
  monthErr: string | null;
  viewDate: Date;
  setViewDate: (d: Date) => void;
  selected: string | null;
  day: any;
  dayErr: string | null;
  tab: CalendarTab;
  setTab: (t: CalendarTab) => void;
  openDay: (d: Date) => void;
  reloadMonth: () => void;
};

export function useCalendar(): CalendarFeed {
  const [monthDays, setMonthDays] = useState<Record<string, DayCounts>>({});
  const [monthStatus, setMonthStatus] = useState<Record<string, string>>({});
  const [monthErr, setMonthErr] = useState<string | null>(null);
  const [viewDate, setViewDate] = useState<Date>(new Date());
  const [selected, setSelected] = useState<string | null>(null);
  const [day, setDay] = useState<any>(null);
  const [dayErr, setDayErr] = useState<string | null>(null);
  const [tab, setTab] = useState<CalendarTab>('reports');

  const loadMonth = useCallback((d: Date) => {
    fetch(calendar(d.getFullYear(), d.getMonth() + 1),
      { headers: authHeaders() })
      .then((r) => r.json())
      .then((j) => {
        if (j && j.success) {
          setMonthDays(j.month_view.days || {});
          // GHOST HOLD item 1 — the per-store statuses RENDER; an unreadable
          // store must never look like a quiet month.
          setMonthStatus(j.month_view.source_status || {});
          setMonthErr(null);
        } else setMonthErr((j && j.error) || 'unavailable');
      })
      .catch((e) => setMonthErr(String(e)));
  }, []);

  useEffect(() => { loadMonth(viewDate); }, [viewDate, loadMonth]);

  const openDay = useCallback((d: Date) => {
    const key = dayKey(d);
    setSelected(key);
    setDay(null);
    setDayErr(null);
    fetch(calendarDay(key), { headers: authHeaders() })
      .then((r) => r.json())
      .then((j) => (j && j.success ? setDay(j.day_view) : setDayErr((j && j.error) || 'unavailable')))
      .catch((e) => setDayErr(String(e)));
  }, []);

  return {
    monthDays, monthStatus, monthErr,
    viewDate, setViewDate,
    selected, day, dayErr,
    tab, setTab,
    openDay,
    reloadMonth: () => loadMonth(viewDate),
  };
}
